import { Icon, LinkButton, MuiAlert, ResponsiveDrawer, Typography, strIsNullOrEmpty } from '@fox/atoms';
import { FlexColumnSpaced, FlexRowSpaced } from '@fox/layout';
import { useEffect } from 'react';
import styled from 'styled-components';
import { useContentContext } from '../../content-context';
import { RsvpButton } from '../events/rsvp-button';
import { formatEventDate } from '../events/util';
import { useEvent } from '../query-hooks/query-implementations';
import { useEventListContext } from './event-list-context';
import { DEFAULT_RSVP_ID } from './util';

export function MobileEventDrawer({
	drawerOpen,
	setDrawerOpen,
	eventId
}: {
	setDrawerOpen: (isOpen: boolean) => void;
	drawerOpen: boolean;
	eventId: string;
}) {
	const { refetch } = useEventListContext();
	return (
		<ResponsiveDrawer
			headerVariant="h5"
			placement="bottom"
			title={'Event details'}
			width="100%"
			height="100%"
			style={{ height: '100%' }}
			open={drawerOpen}
			destroyOnClose
			onClose={() => {
				setDrawerOpen(false);
				refetch();
			}}
		>
			<EventDetails eventId={eventId} isMobile={true} />
		</ResponsiveDrawer>
	);
}

export function DesktopEventDrawer({
	drawerOpen,
	setDrawerOpen,
	eventId
}: {
	setDrawerOpen: (isOpen: boolean) => void;
	drawerOpen: boolean;
	eventId: string;
}) {
	const { refetch } = useEventListContext();
	return (
		<ResponsiveDrawer
			headerVariant="h5"
			placement="right"
			title={'Event details'}
			width={800}
			destroyOnClose
			open={drawerOpen}
			onClose={() => {
				setDrawerOpen(false);
				refetch();
			}}
		>
			<EventDetails eventId={eventId} isMobile={false} />
		</ResponsiveDrawer>
	);
}

const EventWrapper = styled.div`
	padding: 1rem;
	flex-direction: column;
	width: 100%;
	display: flex;
	align-items: space-between;
`;

export function EventDetails({ eventId, isMobile }: { eventId: string; isMobile: boolean }) {
	const { contentId, requestToken } = useContentContext();
	const { rsvpOptions } = useEventListContext();
	const { data: event, error, refetch, loading } = useEvent({ contentId, eventId, requestToken });
	useEffect(() => {
		if (event?.location?.lng && event?.location?.lat && !error) {
			const map = new google.maps.Map(document.getElementById(`map_${eventId}`), {
				center: { lat: event.location.lat, lng: event.location.lng },
				zoom: 14,
				disableDefaultUI: true,
				draggable: false,
				zoomControl: false,
				clickableIcons: false
			});

			const marker = new google.maps.Marker({
				position: { lat: event.location.lat, lng: event.location.lng },
				map: map,
				title: event.name
			});
		}
	}, [event]);
	if (error) return <MuiAlert severity="error">{error}</MuiAlert>;
	if (loading || !event) return <MuiAlert severity="info">Loading...</MuiAlert>;
	return (
		<EventWrapper>
			<FlexColumnSpaced>
				{event.coverImageUrl ? (
					<img
						src={event.coverImageUrl}
						style={{
							width: '100%',
							height: '30rem',
							objectFit: 'cover'
						}}
					/>
				) : null}
				<Typography variant="h4">{event?.name}</Typography>
				<FlexRowSpaced style={{ alignItems: 'center' }}>
					<Icon iconName="CalendarToday" />
					<Typography>{formatEventDate(event?.eventStart, event?.eventEnd)}</Typography>
				</FlexRowSpaced>
				{!strIsNullOrEmpty(event?.location?.formatted) ? (
					<FlexRowSpaced style={{ alignItems: 'center' }}>
						<Icon iconName="LocationOn" />
						{event?.location?.formatted}
					</FlexRowSpaced>
				) : null}
				{!strIsNullOrEmpty(event?.meetingUrl) && event?.rsvpStatus === DEFAULT_RSVP_ID.attending ? (
					<FlexRowSpaced style={{ alignItems: 'center' }}>
						<Icon iconName="Link" />
						<LinkButton
							sx={{ fontSize: 'initial', lineHeight: 'initial' }}
							component="a"
							target="_blank"
							href={event?.meetingUrl}
						>
							Meeting link
						</LinkButton>
					</FlexRowSpaced>
				) : null}
				{!strIsNullOrEmpty(event?.zoomId) && event?.rsvpStatus === DEFAULT_RSVP_ID.attending ? (
					<FlexRowSpaced style={{ alignItems: 'center' }}>
						<Icon iconName="Website" />
						<LinkButton
							sx={{ fontSize: 'initial', lineHeight: 'initial' }}
							component="a"
							target="_blank"
							href={`https://zoom.us/webinar/${event?.zoomId}`}
						>
							Go to zoom webinar
						</LinkButton>
					</FlexRowSpaced>
				) : null}
			</FlexColumnSpaced>
			<FlexColumnSpaced style={{ justifyContent: 'end', flexGrow: 1, marginTop: '1rem' }}>
				<Typography
					className="eventDescription"
					style={{
						opacity: 0.6,
						marginBottom: '1rem',
						whiteSpace: 'pre-wrap'
					}}
				>
					{event?.description}
				</Typography>
				<div
					id={`map_${eventId}`}
					style={{
						height: event?.location?.lat && event?.location?.lng ? '20rem' : '0rem'
					}}
				/>

				<FlexColumnSpaced style={{ width: '100%', justifyContent: 'center', alignItems: 'center' }}>
					<RsvpButton
						eventStart={event.eventStart}
						isMobile={isMobile}
						eventId={event.id}
						refetch={refetch}
						availableTickets={event.availableTickets}
						rsvpFormLink={event.rsvpFormLink}
					/>
					{!strIsNullOrEmpty(event?.rsvpStatus) ? (
						<MuiAlert severity="success" sx={{ backgroundColor: 'transparent' }}>
							{rsvpOptions?.find(option => option.id === event?.rsvpStatus)?.displayName}
						</MuiAlert>
					) : null}
					{event?.availableTickets !== null && event?.availableTickets < 20 ? (
						<MuiAlert severity="info" sx={{ backgroundColor: 'transparent' }}>
							{`${event.availableTickets} ticket(s) left`}
						</MuiAlert>
					) : null}
				</FlexColumnSpaced>
			</FlexColumnSpaced>
		</EventWrapper>
	);
}
